import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaUser, FaEnvelope, FaComment } from "react-icons/fa";

const Contact = () => {
  const [formStatus, setFormStatus] = useState(""); // "success" or "error"
  const [loading, setLoading] = useState(false);

  const inputVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2, duration: 0.6, ease: "easeOut" },
    }),
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus("");

    const formData = new FormData(e.target);

    try {
      const response = await fetch("https://formspree.io/f/xqedybno", {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        setFormStatus("success");
        e.target.reset();
      } else {
        setFormStatus("error");
        console.error("Formspree error:", data);
      }
    } catch (err) {
      setFormStatus("error");
      console.error("Submission error:", err);
    }

    setLoading(false);
  };

  return (
    <section id="contact" className="min-h-screen bg-gray-50 px-6 py-20">
      {/* Heading */}
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-4xl md:text-5xl font-bold text-blue-600 mb-4">
          Contact Us
        </h2>
        <p className="text-gray-700 max-w-2xl mx-auto text-lg md:text-xl">
          Have questions or want to work with us? Send us a message and we’ll get back to you shortly.
        </p>
      </motion.div>

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto bg-white shadow-xl rounded-3xl p-10 space-y-6"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Name */}
        <motion.div
          custom={0}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={inputVariants}
          className="relative"
        >
          <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            name="name"
            placeholder="Your Name"
            required
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition"
          />
        </motion.div>

        {/* Email */}
        <motion.div
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={inputVariants}
          className="relative"
        >
          <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="email"
            name="email"
            placeholder="Your Email"
            required
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition"
          />
        </motion.div>

        {/* Message */}
        <motion.div
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={inputVariants}
          className="relative"
        >
          <FaComment className="absolute left-4 top-4 text-gray-400" />
          <textarea
            name="message"
            rows="5"
            placeholder="Your Message"
            required
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition resize-none"
          ></textarea>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          custom={3}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={inputVariants}
        >
          <button
            type="submit"
            disabled={loading}
            className={`w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-2xl text-lg font-semibold shadow-lg transition-all duration-300 ${
              loading ? "opacity-60 cursor-not-allowed" : "hover:scale-105 hover:shadow-xl"
            }`}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </motion.div>

        {/* Success / Error Message */}
        {formStatus === "success" && (
          <motion.p
            className="text-green-600 text-center font-medium mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            ✅ Your message has been sent successfully!
          </motion.p>
        )}
        {formStatus === "error" && (
          <motion.p
            className="text-red-600 text-center font-medium mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            ❌ Oops! Something went wrong. Please try again.
          </motion.p>
        )}
      </motion.form>
    </section>
  );
};

export default Contact;